import PropTypes from 'prop-types';
import { useId } from '@reach/auto-id';

import Field from '../field';
import Input from '../input';

/**
 * Renders an input field. Supports various types.
 */
export default function InputField({ className, hideOptional, hint, id, label, ...otherProps }) {
  const idWithDefault = useId(id);

  const { disabled, required, type } = otherProps;

  return (
    <Field
      className={className}
      disabled={disabled}
      hideOptional={hideOptional}
      hint={hint}
      id={idWithDefault}
      label={label}
      required={required}
      type={type}
    >
      <Input id={idWithDefault} {...otherProps} />
    </Field>
  );
}

InputField.propTypes = {
  /**
   * ARIA label.
   */
  ariaLabel: PropTypes.string,
  /** Autocomplete attribute. Refer to https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete for more details. */
  autoComplete: PropTypes.oneOf([
    'off',
    'name',
    'honorific-prefix',
    'given-name',
    'additional-name',
    'family-name',
    'honorific-suffix',
    'nickname',
    'email',
    'username',
    'new-password',
    'current-password',
    'one-time-code',
    'organization-title',
    'organization',
    'street-address',
    'address-line1',
    'address-line2',
    'address-line3',
    'address-level4',
    'address-level3',
    'address-level2',
    'address-level1',
    'country',
    'country-name',
    'postal-code',
    'cc-name',
    'cc-given-name',
    'cc-additional-name',
    'cc-number',
    'cc-exp',
    'cc-exp-month',
    'cc-exp-year',
    'cc-csc',
    'cc-type',
    'transation-currency',
    'transaction-amount',
    'language',
    'bday',
    'bday-day',
    'bday-month',
    'bday-year',
    'sex',
    'tel',
    'tel-country-code',
    'tel-national',
    'tel-area-code',
    'tel-local',
    'tel-extension',
    'impp',
    'url',
    'photo',
  ]),
  /**
   * Whether to automatically focus input on mount.
   */
  autoFocus: PropTypes.bool,
  /**
   * Field class name.
   */
  className: PropTypes.string,
  /**
   * Default value. Use for uncontrolled inputs.
   */
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Whether to disable the input.
   */
  disabled: PropTypes.bool,
  /**
   * Whether to hide "Optional" label despite field not being required.
   */
  hideOptional: PropTypes.bool,
  /**
   * Input hint. Displayed below the input.
   */
  hint: PropTypes.node,
  /**
   * Input ID. If not provided, ID will be automatically generated.
   */
  id: PropTypes.string,
  /**
   * Input label. Displayed above the input.
   */
  label: PropTypes.node,
  /**
   * Left icon height in pixels (recommended if icon URL is used).
   */
  leftIconHeight: PropTypes.number,
  /**
   * Left icon, left icon source or left icon URL.
   */
  leftIconSrc: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.node,
    PropTypes.object,
    PropTypes.string,
  ]),
  /**
   * Left icon width in pixels (recommended if icon URL is used).
   */
  leftIconWidth: PropTypes.number,
  /**
   * Input name.
   */
  name: PropTypes.string.isRequired,
  /**
   * Function called on change. Will be called with two attributes: `value` and `event`.
   */
  onChange: PropTypes.func,
  /**
   * Input placeholder.
   */
  placeholder: PropTypes.string,
  /**
   * Whether the input should be required.
   */
  required: PropTypes.bool,
  /**
   * Right icon height in pixels (recommended if icon URL is used).
   */
  rightIconHeight: PropTypes.number,
  /**
   * Right icon, right icon source or right icon URL.
   */
  rightIconSrc: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.node,
    PropTypes.object,
    PropTypes.string,
  ]),
  /**
   * Right icon width in pixels (recommended if icon URL is used).
   */
  rightIconWidth: PropTypes.number,
  /**
   * Input size.
   */
  size: PropTypes.oneOf(['regular', 'small']),
  /**
   * Input type.
   */
  type: PropTypes.oneOf([
    'color',
    'date',
    'datetime-local',
    'email',
    'file',
    'number',
    'password',
    'search',
    'tel',
    'text',
    'time',
    'url',
  ]),
  /**
   * Value. Use for controlled inputs along with `onChange`.
   */
  value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.number]),
};
